/**
 * SIL-1451 - listing-image selection, shared by the public exchange
 * (`apps/exchange`, SSR + the client enhancer) and the customer marketplace
 * card, so both surfaces pick the same image-or-fallback for the same data.
 *
 * Decision (SIL-1449, `docs/exchange/design-handoff/README.md`): the image is
 * the SKU's own or its family (master) image, resolved in SQL by
 * `v_sku_primary_image`. When there is none, generic form-factor art fills the
 * SAME slot (never a collapsed card) and carries no attribution.
 *
 * Pure and dependency-free on purpose: the exchange client bundle imports it,
 * and the unit tests need no DOM.
 */

/** A resolved catalogue photo, as either surface hands it to the renderer. */
export interface ListingImageInput {
  /** Absolute, loadable URL. */
  url: string;
  source?: string | null;
  license?: string | null;
  attribution?: string | null;
}

export type GenericArtKind = 'server-1u' | 'server-2u' | 'server-4u' | 'gpu' | 'generic';

export type ListingImageModel =
  | { kind: 'photo'; url: string; alt: string; credit: string | null }
  | { kind: 'generic'; art: GenericArtKind; alt: string };

const SOURCE_LABELS: Record<string, string> = {
  icecat: 'Icecat',
  commons: 'Wikimedia Commons',
  oem: 'Manufacturer',
};

/**
 * The credit line to show with a photo, or null when the licence requires none.
 *
 * `attribution` is the credit string the ingest stored (SIL-1450's
 * "credit line to display with the image where the licence requires it") and
 * is shown verbatim - for Icecat that is the dated "Database Right data-sheet
 * [Year] Icecat. All rights reserved." the vendor mandates, which only the
 * ingest knows the year of. When an Icecat or Commons row somehow has no
 * attribution we still credit the source (and licence) rather than show an
 * uncredited third-party image. manual / oem / generic / unknown sources with
 * no attribution need none.
 */
export function imageCredit(image: Pick<ListingImageInput, 'source' | 'license' | 'attribution'>): string | null {
  const attribution = image.attribution?.trim();
  if (attribution) return attribution;
  const source = image.source ?? '';
  if (source === 'icecat' || source === 'commons') {
    const license = image.license?.trim();
    return license ? `Image: ${SOURCE_LABELS[source]}, ${license}` : `Image: ${SOURCE_LABELS[source]}`;
  }
  return null;
}

/** Rack height (U) when the text states one, e.g. "PowerEdge XE9680 6U". */
export function rackUnitsFromText(text: string | null | undefined): number | null {
  if (!text) return null;
  const match = /(?:^|[^a-z0-9])(\d{1,2})\s?U(?![a-z0-9])/i.exec(text);
  if (!match) return null;
  const units = Number(match[1]);
  return units >= 1 && units <= 12 ? units : null;
}

function serverArt(rackUnits: number | null, gpuDense: boolean): GenericArtKind {
  if (rackUnits !== null) return rackUnits <= 1 ? 'server-1u' : rackUnits <= 2 ? 'server-2u' : 'server-4u';
  // Unstated height: GPU servers are 4U-and-up chassis; plain compute is 2U.
  return gpuDense ? 'server-4u' : 'server-2u';
}

export interface GenericArtInput {
  /** Asset taxonomy id of a single-class listing, e.g. 'server' / 'gpu'. */
  assetClassId?: string | null;
  /** Distinct asset classes behind the listing (a lot may mix classes). */
  assetTypeIds?: readonly string[];
  /** Free-text names that may state a rack height. */
  names?: ReadonlyArray<string | null | undefined>;
  /** True when the listing is known to be GPU-bearing. */
  hasGpus?: boolean;
  /** True when it is known to be a whole server/node (not a loose part). */
  isServer?: boolean;
}

/**
 * Which generic art stands in for a listing with no image. Order matters:
 * a mixed-class lot gets the neutral art (no single form factor is honest),
 * a standalone GPU gets the PCIe card, anything server-shaped gets a chassis
 * sized by its stated rack height, and everything else the neutral art.
 */
export function genericArtFor(input: GenericArtInput): GenericArtKind {
  const classes = new Set<string>();
  if (input.assetClassId) classes.add(input.assetClassId);
  for (const id of input.assetTypeIds ?? []) classes.add(id);
  if (classes.size > 1) return 'generic';

  const only = classes.size === 1 ? [...classes][0] : null;
  if (only === 'gpu') return 'gpu';

  const rackUnits = (input.names ?? []).map(rackUnitsFromText).find((u) => u !== null) ?? null;
  if (only === 'server' || input.isServer || rackUnits !== null) {
    return serverArt(rackUnits, input.hasGpus === true);
  }
  return 'generic';
}

/** Photo when a usable url is present, else the generic art. Never null: the
 * image slot always renders so card grids stay aligned. */
export function listingImageModel(args: {
  image: ListingImageInput | null | undefined;
  /** Alt text: the family/SKU display name. */
  alt: string;
  art: GenericArtKind;
}): ListingImageModel {
  const { image, alt, art } = args;
  if (image && image.url) {
    return { kind: 'photo', url: image.url, alt, credit: imageCredit(image) };
  }
  return { kind: 'generic', art, alt };
}

// Inline art. Stroke-only so it follows `currentColor` (the host sets
// `text-muted-foreground`), which is what makes it theme-aware in light and
// dark with no per-theme asset. viewBox is 160x120 for every kind.
const STROKE = 'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';

function chassis(heightPx: number, bays: number): string {
  const y = Math.round((120 - heightPx) / 2);
  const slots: string[] = [];
  const left = 34;
  const right = 100;
  const step = (right - left) / bays;
  for (let i = 0; i < bays; i += 1) {
    const x = Math.round(left + i * step);
    slots.push(`<rect x="${x}" y="${y + 6}" width="${Math.max(4, Math.round(step) - 4)}" height="${heightPx - 12}" rx="1.5"/>`);
  }
  return (
    `<g ${STROKE}>` +
    `<rect x="14" y="${y}" width="132" height="${heightPx}" rx="4"/>` +
    slots.join('') +
    `<circle cx="122" cy="${y + heightPx / 2}" r="4"/>` +
    `<path d="M132 ${y + 8}v${heightPx - 16}"/>` +
    '</g>'
  );
}

/** Inner SVG markup (viewBox 0 0 160 120) for each art kind. */
export const GENERIC_ART_MARKUP: Record<GenericArtKind, string> = {
  'server-1u': chassis(22, 6),
  'server-2u': chassis(40, 6),
  'server-4u': chassis(76, 5),
  gpu:
    `<g ${STROKE}>` +
    '<rect x="14" y="30" width="132" height="54" rx="4"/>' +
    '<circle cx="52" cy="57" r="17"/><circle cx="52" cy="57" r="5"/>' +
    '<circle cx="104" cy="57" r="17"/><circle cx="104" cy="57" r="5"/>' +
    '<path d="M24 84v10h18v-10M54 84v10h8v-10M66 84v10h8v-10M78 84v10h8v-10M90 84v10h8v-10"/>' +
    '<path d="M146 36v42"/>' +
    '</g>',
  generic:
    `<g ${STROKE}>` +
    '<path d="M80 22l46 25v26L80 98 34 73V47z"/>' +
    '<path d="M34 47l46 25 46-25M80 72v26"/>' +
    '</g>',
};

export const GENERIC_ART_LABELS: Record<GenericArtKind, string> = {
  'server-1u': '1U rack server',
  'server-2u': '2U rack server',
  'server-4u': '4U rack server',
  gpu: 'PCIe GPU card',
  generic: 'Compute hardware',
};
